import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { ScanService } from '../services/fixedAssetCount.service';
import { useAuth } from '../context/AuthContext';
import { safeReturnPath } from '../utils/roleLanding';

/**
 * The page a printed QR label opens: /q/:type/:id. It asks the server where that record lives (and whether this user
 * may open it), then moves there. A signed-out visitor is bounced to login and brought back by ProtectedRoute.
 */
const QrLanding: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { selectedInstitutionId, selectInstitution } = useAuth();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    if (!type || !id) { setFailed(true); return undefined; }
    ScanService.resolve(type, id)
      .then(async r => {
        if (!live) return;
        const path = safeReturnPath(r.path);
        if (!path) { setFailed(true); return; }
        // Switch to the record's company first if the user works in several, then reload into it so every page
        // refetches for that company (the same full reload the header switcher does).
        if (r.institutionId && selectedInstitutionId !== r.institutionId) {
          const result = await selectInstitution(r.institutionId);
          if (!live) return;
          if (!result.success) { setFailed(true); return; }
          window.location.assign(path);
          return;
        }
        navigate(path, { replace: true });
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
    // The company is read once when the label resolves; switching it mid-flight must not restart the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id, navigate]);

  if (failed) {
    return (
      <Box sx={{ p: 3, maxWidth: 560 }} data-testid="qr-landing-failed">
        <Typography variant="h5" gutterBottom>Label not found</Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This label is not for a record you can open. It may belong to another company, or you may not have access.
        </Alert>
        <Button component={RouterLink} to="/home" variant="outlined">Go to my dashboard</Button>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }} data-testid="qr-landing-loading">
      <CircularProgress size={22} />
      <Typography>Opening the record...</Typography>
    </Box>
  );
};

export default QrLanding;
